import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { Spinner } from "@/components/ui/Spinner";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { extractWypisDoo } from "../../api";
import { useWypisOrderMutations } from "../../hooks";
import type { WypisOrder } from "../../types";
import { AttachExistingFeeField } from "../DooFeeAttach";

// Creates a normal /official-fees request (pending → approval → small-account pays).
// Office name/address come from the starostwo; the account number is manual (the
// Office registry has none) with the last-used one for this office prefilled.
// A DOO PDF can prefill everything via AI (amount, account, title with DOO number).
// Alternatively an EXISTING own fee from /official-fees can be pinned instead
// (mode=existing) — the DOO upload still stores the PDF on the order either way.
export function RequestFeeModal({ order, onClose }: { order: WypisOrder; onClose: () => void }) {
  const { requestFee } = useWypisOrderMutations(order.id);
  const [amount, setAmount] = useState("");
  const [account, setAccount] = useState(order.suggested_account_number || "");
  const [transferName, setTransferName] = useState(`Wypisy z EGiB — ${order.number}`);
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");
  const [dooWarning, setDooWarning] = useState("");
  const [dooFiles, setDooFiles] = useState<File[]>([]);
  const [existingFee, setExistingFee] = useState("");

  const dooExtract = useMutation({
    mutationFn: (file: File) => extractWypisDoo(order.id, file),
    onSuccess: (d) => {
      setDooWarning("");
      setAmount(d.amount);
      if (d.account_number) setAccount(d.account_number);
      // Tytuł przelewu: jeśli DOO podaje własny tytuł płatności („Opłata GA/877/2026"),
      // przepisujemy go DOKŁADNIE — urzędy wymagają zgodności co do znaku. Dopiero gdy
      // go nie ma, składamy własny: „DOO - nr <numer>", znak sprawy, nasz numer zamówienia.
      const dooNum = (d.doo_number || "").replace(/^\s*DOO[\s.:#-]*/i, "").trim();
      setTransferName(
        d.payment_title ||
          [
            `DOO - nr ${dooNum || "—"}`,
            d.case_sign || "",
            order.number,
          ].filter(Boolean).join(" · "),
      );
      const officeInfo = [d.office_name, d.office_address].filter(Boolean).join(", ");
      const parts = [
        d.doo_number ? `DOO ${d.doo_number}` : "",
        d.case_sign ? `znak sprawy: ${d.case_sign}` : "",
        `zamówienie: ${order.number}`,
        officeInfo ? `odbiorca: ${officeInfo}` : "",
      ].filter(Boolean);
      if (parts.length) setDescription(parts.join(" · "));
      if (!d.payment_title && !d.doo_number) {
        setDooWarning("Nie znaleziono tytułu płatności ani numeru DOO w dokumencie — uzupełnij tytuł ręcznie.");
      }
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { errors?: { detail?: unknown } } } })
        ?.response?.data?.errors?.detail;
      setDooWarning(typeof detail === "string" ? detail : "Nie udało się odczytać DOO — wypełnij pola ręcznie.");
    },
  });

  // Reszta formularza (kwota/konto/tytuł/opis) pojawia się dopiero PO analizie DOO —
  // sukces uzupełnia pola, błąd/brak DOO odsłania je do ręcznego wpisania.
  const showForm = dooExtract.isSuccess || dooExtract.isError;

  const submit = () => {
    setError("");
    requestFee.mutate(
      {
        id: order.id,
        payload: existingFee
          ? { mode: "existing" as const, fee: existingFee }
          : {
              amount,
              office_account_number: account,
              transfer_name: transferName || undefined,
              description: description || undefined,
            },
      },
      {
        onSuccess: onClose,
        onError: (e: unknown) => {
          const detail =
            (e as { response?: { data?: { errors?: { detail?: unknown } } } })?.response?.data
              ?.errors?.detail;
          setError(
            typeof detail === "string" ? detail : detail ? JSON.stringify(detail) : "Nie udało się złożyć prośby.",
          );
        },
      },
    );
  };

  return (
    <Modal open onClose={onClose} title={`Prośba o opłatę — ${order.number}`}>
      <div className="space-y-3">
        <div className="text-xs text-[var(--color-text-secondary)]">
          Prośba trafi do <b>/official-fees</b> normalnym obiegiem: akceptacja dyrektora/koordynatora,
          potem opłacenie z konta podręcznego. Urząd: <b>{order.starostwo_name}</b>. Projekt opłaty
          zostanie przypisany automatycznie (największa liczba działek w zamówieniu).
        </div>
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs">
            <Sparkles size={14} className="shrink-0" />
            <span>
              <b>PDF z DOO</b> — kwota, konto i tytuł uzupełnią się automatycznie (AI)
            </span>
          </div>
          <FileDropZone
            files={dooFiles}
            accept=".pdf"
            onFilesChange={(fs) => {
              setDooFiles(fs);
              if (fs[0]) dooExtract.mutate(fs[0]);
            }}
          />
          {dooExtract.isPending && (
            <div className="flex items-center gap-2 text-xs text-[var(--color-text-muted)]">
              <Spinner /> Analizuję DOO…
            </div>
          )}
          {dooWarning && <div className="text-xs text-amber-600">{dooWarning}</div>}
        </div>

        <AttachExistingFeeField module="wypis" value={existingFee} onChange={setExistingFee} />

        {existingFee ? (
          <>
            {error && <div className="text-xs text-red-600">{error}</div>}
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={onClose}>Anuluj</Button>
              <Button onClick={submit} loading={requestFee.isPending}>
                Podepnij opłatę
              </Button>
            </div>
          </>
        ) : !showForm ? (
          <div className="text-xs text-[var(--color-text-muted)] py-2">
            {dooExtract.isPending
              ? "Trwa analiza DOO — pola opłaty pojawią się po jej zakończeniu."
              : "Wgraj PDF z DOO powyżej — po analizie uzupełnimy kwotę, konto i tytuł przelewu. (Jeśli DOO nie da się odczytać, pola odsłonią się do ręcznego wpisania.)"}
          </div>
        ) : (
          <>
            <label className="block">
              <span className="section-label">Kwota [PLN] *</span>
              <input
                className="input-base mt-1"
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </label>
            <label className="block">
              <span className="section-label">Numer konta urzędu * {order.suggested_account_number ? "(podpowiedź: ostatnio użyte)" : ""}</span>
              <input
                className="input-base mt-1 font-mono"
                value={account}
                placeholder="26 cyfr (PL…)"
                onChange={(e) => setAccount(e.target.value)}
              />
            </label>
            <label className="block">
              <span className="section-label">Tytuł przelewu</span>
              <input className="input-base mt-1" value={transferName} onChange={(e) => setTransferName(e.target.value)} />
            </label>
            <label className="block">
              <span className="section-label">Opis (opcjonalnie)</span>
              <textarea className="input-base mt-1 h-14" value={description} onChange={(e) => setDescription(e.target.value)} />
            </label>
            {error && <div className="text-xs text-red-600">{error}</div>}
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={onClose}>Anuluj</Button>
              <Button onClick={submit} loading={requestFee.isPending} disabled={!amount || !account}>
                Złóż prośbę o opłatę
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
